import { Children, isValidElement, PropsWithChildren, ReactElement, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useButton, useDisclosure, mergeProps, useFocusRing, useHover } from 'react-aria';
import { useDisclosureState } from 'react-stately';
import '../Consts.stylex';
import { Transitions } from '../Consts.stylex';
import '../theme/Colors.stylex';
import { ColorNeutral } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Gap, Padding } from '../theme/Spaces.stylex';
import { Icon } from '../images-and-icons/Icon';
import { Title } from '../typography/Title';

// MARK: styles
// ============

// MARK: DisclosureTableLine
// ============
export type DisclosureTableLineProps = {
  children: React.ReactNode;
};
export function DisclosureTableLine({
  children
}: DisclosureTableLineProps) {
  return <div className="display-x78zum5 gap-x851vlu borderBottomWidth-x1co6499 borderBottomStyle-x1q0q8m5 borderBottomColor-x1kze5kp" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:107">
      <div className="display-x78zum5 flexDirection-xdt5ytf alignItems-x1cy8zhl paddingLeft-xxhin2k gap-x1lrnx4e" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:114">
        <div className="width-x197jcof flexGrow-x1iyjqo2 backgroundColor-xe58vim" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:121" />
        <div className="width-x51ohtg height-xqu0tyb borderRadius-x16rqkct backgroundColor-xe58vim marginLeft-x1bmqhc4" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:126" />
        <div className="width-x197jcof flexGrow-x1iyjqo2 backgroundColor-xe58vim" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:121" />
      </div>
      <div className="flexGrow-x1iyjqo2 paddingBlock-xcp6hss" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:133">{children}</div>
    </div>;
}

// MARK: DisclosureTitle
// ============
export type DisclosureTitleProps = PropsWithChildren;
export function DisclosureTitle(props: DisclosureTitleProps): ReactElement {
  return <Title level={5} color={ColorNeutral.textAccent} weight="bold">
      {props.children}
    </Title>;
}

// MARK: DisclosureSeparator
// ============
export type DisclosureSeparatorProps = {
  hovered: boolean;
  expanded: boolean;
};
export function DisclosureSeparator(props: DisclosureSeparatorProps): ReactElement {
  return <div {...{
    0: {
      className: "height-x28ko6u backgroundColor-xe58vim",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:80"
    },
    1: {
      className: "height-x28ko6u backgroundColor-xw77ynr",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:80; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:84"
    }
  }[!!(props.hovered && !props.expanded) << 0]} />;
}

// MARK: DisclosureArrow
// ============
export type DisclosureArrowProps = {
  hovered: boolean;
  expanded: boolean;
};
export function DisclosureArrow(props: DisclosureArrowProps): ReactElement {
  return <div {...{
    0: {
      className: "display-x78zum5 transform-x7p49u4 transition-x1hgg8ub transitionProperty-x11xpdln transitionDuration-x13dflua",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:63"
    },
    1: {
      className: "display-x78zum5 transition-x1hgg8ub transitionProperty-x11xpdln transitionDuration-x13dflua transform-x1iffjtl",
      "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:63; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:70"
    }
  }[!!props.expanded << 0]}>
      <Icon name="chevron_right" fill={props.hovered ? ColorNeutral.iconAccent : ColorNeutral.iconDefault} width={16} height={16} />
    </div>;
}
function childrenContainDisclosureTableLine(children: React.ReactNode): boolean {
  return Children.toArray(children).some(child => {
    if (!isValidElement(child)) {
      return false;
    }
    return child.type === DisclosureTableLine ? true : childrenContainDisclosureTableLine(child.props.children);
  });
}

// MARK: Disclosure
// ============
export type DisclosureProps = {
  children?: React.ReactNode;
  title?: string;
  headerElement?: React.ReactNode;
  headerEndElement?: React.ReactNode;
  defaultExpanded?: boolean;
  hideBottomSeparator?: boolean;
  hideTopSeparator?: boolean;
  expandable?: boolean;
};
export function Disclosure({
  children,
  title,
  headerElement: headerElement,
  headerEndElement: headerEndElement,
  hideTopSeparator = false,
  hideBottomSeparator = false,
  defaultExpanded = false,
  expandable = true
}: DisclosureProps) {
  const hasChildren = Children.count(children) > 0;
  const isExpandable = hasChildren && expandable;
  const hasTableLineChildren = childrenContainDisclosureTableLine(children);
  const state = useDisclosureState({
    defaultExpanded: isExpandable && defaultExpanded
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const {
    buttonProps: triggerProps,
    panelProps
  } = useDisclosure({
    isDisabled: !isExpandable
  }, state, panelRef);
  const {
    buttonProps
  } = useButton(triggerProps, triggerRef);
  const {
    focusProps,
    isFocusVisible
  } = useFocusRing();
  const {
    hoverProps,
    isHovered
  } = useHover({
    isDisabled: !isExpandable
  });
  const shouldDisplayHeaderEndElement = headerEndElement && (!state.isExpanded || !hasTableLineChildren);
  return <div className="display-x78zum5 flexDirection-xdt5ytf width-xh8yej3" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:29">
      {hideTopSeparator ? null : <DisclosureSeparator hovered={isHovered} expanded={state.isExpanded} />}
      <div ref={triggerRef} {...mergeProps(isExpandable ? buttonProps : {}, focusProps, hoverProps)} {...{
      0: {
        className: "display-x78zum5 alignItems-x6s0dn4 justifyContent-x1qughib position-x1n2onr6 paddingBlock-xcp6hss paddingInline-x1exdpdg gap-xw718zh backgroundColor-xjbqb8w borderStyle-xng3xce outline-x1a2a7pz",
        "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:34"
      },
      1: {
        className: "display-x78zum5 alignItems-x6s0dn4 justifyContent-x1qughib position-x1n2onr6 paddingBlock-xcp6hss paddingInline-x1exdpdg gap-xw718zh backgroundColor-xjbqb8w borderStyle-xng3xce outline-x1a2a7pz cursor-x1ypdohk",
        "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:34; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:46"
      }
    }[!!isExpandable << 0]}>
        <div {...{
        0: {
          className: "display-x78zum5 alignItems-x6s0dn4 gap-xt0mmm2 flex-x98rzlu minWidth-xeuugli",
          "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:56"
        },
        1: {
          className: "display-x78zum5 alignItems-x6s0dn4 gap-xt0mmm2 flex-x98rzlu minWidth-xeuugli outline-x1v0ymhg outlineOffset-x2wh2y9 borderRadius-x1cum3z5",
          "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:56; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:51"
        }
      }[!!isFocusVisible << 0]}>
          {title != null ? <DisclosureTitle>{title}</DisclosureTitle> : null}
          {headerElement}
          {isExpandable ? <DisclosureArrow hovered={isHovered} expanded={state.isExpanded} /> : null}
        </div>
        {shouldDisplayHeaderEndElement && <div className="display-x78zum5 alignItems-x6s0dn4 gap-xw718zh marginLeft-x8x9d4c flexShrink-x2lah0s" data-style-src="wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:73">{headerEndElement}</div>}
      </div>
      {hideBottomSeparator ? null : <DisclosureSeparator hovered={isHovered} expanded={state.isExpanded} />}
      {isExpandable && <div {...{
      0: {
        className: "display-xrvj5dj gridTemplateRows-xihq33y transition-x1hgg8ub transitionProperty-x1qn9uv2 transitionDuration-x1rvut6x",
        "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:87"
      },
      1: {
        className: "display-xrvj5dj transition-x1hgg8ub transitionProperty-x1qn9uv2 transitionDuration-x1rvut6x gridTemplateRows-x1tu4anv",
        "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:87; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:94"
      }
    }[!!state.isExpanded << 0]}>
          <div ref={panelRef} {...panelProps} {...{
        0: {
          className: "overflow-xb3r6kr opacity-xg01cxk transitionProperty-x19991ni transitionDuration-x13dflua",
          "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:97"
        },
        1: {
          className: "overflow-xb3r6kr transitionProperty-x19991ni transitionDuration-x13dflua opacity-x1hc1fzr transitionDelay-x5zdck6",
          "data-style-src": "wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:97; wino-gc:src/resources/data-visualizations/CanaryDisclosure.tsx:103"
        }
      }[!!state.isExpanded << 0]}>
            {children}
          </div>
        </div>}
    </div>;
}